"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { FaceMesh } from "@mediapipe/face_mesh";
import { Camera } from "@mediapipe/camera_utils";

const MAX_SCORE = 100;
const EAR_THRESHOLD = 0.25;
const EAR_CONSEC_FRAMES = 2;
const HEAD_TURN_THRESHOLD_X = 0.08;
const SMILE_THRESHOLD = 0.4;

const LEFT_EYE_INDICES = [33, 160, 158, 133, 153, 144];
const RIGHT_EYE_INDICES = [362, 385, 387, 263, 373, 380];
const MOUTH_INDICES = {
  top: 13,
  bottom: 14,
  left: 61,
  right: 291,
};
const NOSE_TIP_INDEX = 1;

interface LiveCheckProps {
  showModal: boolean;
  onCloseModal: () => void;
  _setScore: React.Dispatch<React.SetStateAction<number>>;
}

export default function LiveCheck({
  showModal,
  onCloseModal,
  _setScore,
}: LiveCheckProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const faceMeshRef = useRef<FaceMesh | null>(null);
  const cameraRef = useRef<Camera | null>(null);

  const livenessState = useRef({
    isLoading: true,
    instruction: "Loading model...",
    faceDetected: false,
    blinkDetected: false,
    headTurnDetected: false,
    smileDetected: false,
    blinkFrameCounter: 0,
    initialNoseX: 0,
    boxWidth: 0,
    lastScore: 0, // Track the last score to avoid unnecessary updates
  });

  const [score, setScore] = useState(0);
  const [displayState, setDisplayState] = useState({
    isLoading: true,
    instruction: "Loading model...",
    faceDetected: false,
  });

  // --- Liveness Logic ---

  const calculateDistance = (p1: any, p2: any) =>
    Math.hypot(p1.x - p2.x, p1.y - p2.y);

  const calculateEAR = (eyeIndices: number[], landmarks: any[]) => {
    const p1 = landmarks[eyeIndices[0]];
    const p2 = landmarks[eyeIndices[1]];
    const p3 = landmarks[eyeIndices[2]];
    const p4 = landmarks[eyeIndices[3]];
    const p5 = landmarks[eyeIndices[4]];
    const p6 = landmarks[eyeIndices[5]];

    const verticalDist = calculateDistance(p2, p6) + calculateDistance(p3, p5);
    const horizontalDist = 2 * calculateDistance(p1, p4);

    return verticalDist / horizontalDist;
  };

  const calculateMAR = (landmarks: any[]) => {
    const mouthLeft = landmarks[MOUTH_INDICES.left];
    const mouthRight = landmarks[MOUTH_INDICES.right];
    const mouthTop = landmarks[MOUTH_INDICES.top];
    const mouthBottom = landmarks[MOUTH_INDICES.bottom];

    const mouthWidth = calculateDistance(mouthLeft, mouthRight);
    const mouthHeight = calculateDistance(mouthTop, mouthBottom);

    return mouthHeight / mouthWidth;
  };

  // Use useEffect to update parent score only when local score changes
  useEffect(() => {
    _setScore(score);
  }, [score, _setScore]);

  const updateScore = useCallback((points: number) => {
    setScore((prevScore) => {
      const newScore = Math.min(prevScore + points, MAX_SCORE);

      // Only update if score actually changed
      if (newScore !== prevScore) {
        livenessState.current.lastScore = newScore;
        return newScore;
      }
      return prevScore;
    });
  }, []);

  const processLivenessDetection = useCallback(
    (landmarks: any[]) => {
      if (!landmarks || landmarks.length === 0) {
        const newState = {
          faceDetected: false,
          instruction:
            "No face detected. Please position your face in the center.",
        };

        setDisplayState((prev) =>
          prev.faceDetected === newState.faceDetected &&
          prev.instruction === newState.instruction
            ? prev
            : { ...prev, ...newState }
        );
        livenessState.current.faceDetected = false;
        return;
      }

      // Calculate bounding box width from landmarks
      const xs = landmarks.map((l: any) => l.x);
      const ys = landmarks.map((l: any) => l.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const boxWidth = maxX - minX;

      let scoreToAdd = 0;
      let newInstruction = livenessState.current.instruction;
      let shouldUpdateDisplay = false;

      if (!livenessState.current.faceDetected) {
        livenessState.current.faceDetected = true;
        livenessState.current.initialNoseX = landmarks[NOSE_TIP_INDEX].x;
        livenessState.current.boxWidth = boxWidth;
        newInstruction = "Please blink your eyes.";
        scoreToAdd = 25;
        shouldUpdateDisplay = true;
      }

      // 1. Blink Detection
      if (
        !livenessState.current.blinkDetected &&
        livenessState.current.faceDetected
      ) {
        const leftEAR = calculateEAR(LEFT_EYE_INDICES, landmarks);
        const rightEAR = calculateEAR(RIGHT_EYE_INDICES, landmarks);
        const avgEAR = (leftEAR + rightEAR) / 2;

        if (avgEAR < EAR_THRESHOLD) {
          livenessState.current.blinkFrameCounter++;
        } else {
          if (livenessState.current.blinkFrameCounter >= EAR_CONSEC_FRAMES) {
            livenessState.current.blinkDetected = true;
            newInstruction = "Great! Now please smile.";
            scoreToAdd = 25;
            shouldUpdateDisplay = true;
          }
          livenessState.current.blinkFrameCounter = 0;
        }
      }
      // 2. Smile Detection
      else if (
        !livenessState.current.smileDetected &&
        livenessState.current.blinkDetected
      ) {
        const mar = calculateMAR(landmarks);
        if (mar > SMILE_THRESHOLD) {
          livenessState.current.smileDetected = true;
          newInstruction = "Awesome! Now turn your head slightly.";
          scoreToAdd = 25;
          shouldUpdateDisplay = true;
        }
      }
      // 3. Head Turn Detection
      else if (
        !livenessState.current.headTurnDetected &&
        livenessState.current.smileDetected
      ) {
        const currentNoseX = landmarks[NOSE_TIP_INDEX].x;
        const headMoveDelta =
          Math.abs(currentNoseX - livenessState.current.initialNoseX) /
          livenessState.current.boxWidth;

        if (headMoveDelta > HEAD_TURN_THRESHOLD_X) {
          livenessState.current.headTurnDetected = true;
          newInstruction = "Liveness check complete!";
          scoreToAdd = 25;
          shouldUpdateDisplay = true;
        }
      }

      if (scoreToAdd > 0) {
        updateScore(scoreToAdd);
      }

      if (
        shouldUpdateDisplay ||
        newInstruction !== livenessState.current.instruction
      ) {
        setDisplayState((prev) => ({
          ...prev,
          instruction: newInstruction,
          faceDetected: livenessState.current.faceDetected,
        }));
        livenessState.current.instruction = newInstruction;
      }
    },
    [updateScore]
  );

  const onResults = useCallback(
    (results: any) => {
      const canvasCtx = canvasRef.current?.getContext("2d");
      if (!canvasCtx || !canvasRef.current) return;

      canvasCtx.clearRect(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );

      canvasCtx.drawImage(
        results.image,
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );

      if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
        const landmarks = results.multiFaceLandmarks[0];

        canvasCtx.fillStyle = "rgba(0,255,0,0.5)";
        for (let point of landmarks) {
          canvasCtx.beginPath();
          canvasCtx.arc(
            point.x * canvasRef.current!.width,
            point.y * canvasRef.current!.height,
            1.5,
            0,
            2 * Math.PI
          );
          canvasCtx.fill();
        }

        processLivenessDetection(landmarks);
      } else {
        if (livenessState.current.faceDetected) {
          livenessState.current.faceDetected = false;
          setDisplayState((prev) => ({
            ...prev,
            faceDetected: false,
            instruction:
              "No face detected. Please position your face in the center.",
          }));
        }
      }
    },
    [processLivenessDetection]
  );

  useEffect(() => {
    if (!showModal) return;

    const initializeFaceDetection = async () => {
      try {
        const faceMesh = new FaceMesh({
          locateFile: (file) =>
            `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
        });

        faceMesh.setOptions({
          maxNumFaces: 1,
          refineLandmarks: true,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });

        faceMesh.onResults(onResults);
        faceMeshRef.current = faceMesh;

        // Initialize camera
        if (videoRef.current) {
          const camera = new Camera(videoRef.current, {
            onFrame: async () => {
              await faceMesh.send({ image: videoRef.current! });
            },
            width: 640,
            height: 480,
          });
          camera.start();
          cameraRef.current = camera;
        }

        livenessState.current.isLoading = false;
        setDisplayState((prev) => ({
          ...prev,
          isLoading: false,
          instruction: "Please position your face in the center.",
        }));
      } catch (error) {
        console.error("Error initializing face detection:", error);
        setDisplayState((prev) => ({
          ...prev,
          isLoading: false,
          instruction: "Error loading model. Please try again.",
        }));
      }
    };

    initializeFaceDetection();

    return () => {
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
      if (faceMeshRef.current) {
        faceMeshRef.current.close();
      }
    };
  }, [showModal, onResults]);

  useEffect(() => {
    if (showModal) {
      setScore(0);
      setDisplayState({
        isLoading: true,
        instruction: "Loading model...",
        faceDetected: false,
      });
      livenessState.current = {
        isLoading: true,
        instruction: "Loading model...",
        faceDetected: false,
        blinkDetected: false,
        headTurnDetected: false,
        smileDetected: false,
        blinkFrameCounter: 0,
        initialNoseX: 0,
        boxWidth: 0,
        lastScore: 0,
      };
    }
  }, [showModal]);

  useEffect(() => {
    if (score === MAX_SCORE) {
      setTimeout(() => {
        handleClose();
        // onCloseModal();
      }, 1000);
    }
  }, [score, onCloseModal]);

  const handleClose = () => {
    console.log("Closing modal and stopping camera");

    if (cameraRef.current) {
      try {
        cameraRef.current.stop();
      } catch (err) {
        console.warn("Error stopping MediaPipe camera:", err);
      }
      cameraRef.current = null;
    }

    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }

    if (faceMeshRef.current) {
      try {
        faceMeshRef.current.close();
      } catch (err) {
        console.warn("Error closing FaceMesh:", err);
      }
      faceMeshRef.current = null;
    }

    onCloseModal();
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2 className="text-2xl font-bold text-center mb-4">
          Liveliness Check
        </h2>

        <div className="relative mx-auto w-full max-w-2xl">
          <video
            ref={videoRef}
            className="rounded-2xl shadow-md w-full"
            autoPlay
            playsInline
            muted
            width={640}
            height={480}
          ></video>
          <canvas
            ref={canvasRef}
            className="absolute top-0 left-0 w-full h-full"
            width={640}
            height={480}
          ></canvas>

          {displayState.isLoading && (
            <div
              className={`absolute inset-0 flex flex-col items-center justify-center rounded-2xl ${
                displayState.instruction === "Loading model..." &&
                "bg-black bg-opacity-70"
              }`}
            >
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
              <p className="text-white text-lg">{displayState.instruction}</p>
            </div>
          )}
        </div>

        <div className="mt-4 text-center">
          <p className="text-lg font-semibold">
            {displayState.faceDetected
              ? "✅ Face Detected"
              : "❌ No Face Detected"}
          </p>
          <p className="text-md mt-2 min-h-6">{displayState.instruction}</p>
        </div>

        <div className="flex justify-center mt-4">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full" viewBox="0 0 36 36">
              <path
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#EEE"
                strokeWidth="3"
              />
              <path
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#4CAF50"
                strokeWidth="3"
                strokeDasharray={`${score}, 100`}
              />
              <text
                x="18"
                y="20.5"
                textAnchor="middle"
                fill="#4CAF50"
                fontSize="8"
                fontWeight="bold"
              >
                {score}%
              </text>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
